/**
 * Base invitation types used across the application.
 * These are the core types that don't depend on platform-specific logic.
 */

/**
 * Invitation type - determines which UI section displays this invitation.
 * - 'participant': For inviting program participants
 * - 'orgAdmin': For inviting organization administrators
 * - 'orgAdminPrimary': For inviting primary contact organization administrators
 * - 'participant_friend': For participant-to-friend invitations
 */
export type InvitationType =
  | "participant"
  | "orgAdmin"
  | "orgAdminPrimary"
  | "participant_friend";

export const INVITATION_TYPE = {
  PARTICIPANT: "participant",
  ORG_ADMIN: "orgAdmin",
  ORG_ADMIN_PRIMARY: "orgAdminPrimary",
  PARTICIPANT_FRIEND: "participant_friend",
} as const satisfies Record<string, InvitationType>;

/**
 * Display options for controlling invitation UI behavior.
 * Following the same pattern as Action.display_options.
 */
export type InvitationDisplayOptions = {
  /** Optional CTA label override */
  cta_label?: string;
  /** Optional quiz ID for fit quiz before signup */
  quiz_id?: number;
};

export type Invitation = {
  id: number;
  shortcode: string;
  name: string;
  /** Maximum number of times this invitation can be used (null = unlimited) */
  max_uses: number | null;
  /** Type of invitation: participant or organization admin */
  type: InvitationType;
  /** Display options for controlling UI behavior */
  display_options: InvitationDisplayOptions | null;
  program_id: number;
  program_name: string;
  program_description: string;
  owner_id: number;
  owner_type: string;
  owner_display: string;
};

/**
 * How the invitation was claimed.
 * - direct_link: User clicked a direct invitation link
 * - code_entry: User manually entered an invitation code
 * - lookup: User found the invitation via organization lookup
 */
export type ClaimedVia = "direct_link" | "code_entry" | "lookup";

/**
 * Friend invitation form data (single invite with email confirmation)
 */
export type FriendInvitationFormData = {
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
};

/**
 * Result from sending friend invitations API
 */
export type SendFriendInvitationsResult = {
  sent_count: number;
  skipped: Array<{ email: string; reason: string }>;
  errors: Array<{ email: string; error: string }>;
};

/**
 * User who claimed an invitation
 */
export type InvitationRecordClaimedBy = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
};

/**
 * Invitation data needed for deletion dialog
 */
export type DeletionInvitation = {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
};

/**
 * A pending invitation sent to the user's email that hasn't been claimed yet.
 * Used on the dashboard to show claimable invitations.
 */
export type PendingInvitation = {
  id: number;
  shortcode: string;
  invitation_type: InvitationType;
  program_id: number;
  program_name: string | null;
  organization_name: string | null;
  created_at: string;
};

/**
 * Template invitation - a reusable blueprint for creating invitation instances.
 * Templates have is_template=True and are used to create org-specific or user-specific invitations.
 */
export type InvitationTemplate = {
  id: number;
  shortcode: string;
  name: string;
  type: InvitationType;
};

/**
 * Record of an invitation that was sent or claimed
 */
export type InvitationRecord = {
  id: number;
  /** Shortcode of the invitation this record belongs to */
  invitation_shortcode: string;
  sent_to_email: string | null;
  sent_to_first_name: string;
  sent_to_last_name: string;
  claimed_status: "pending" | "claimed" | "cancelled";
  claimed_by: InvitationRecordClaimedBy | null;
  claimed_via: ClaimedVia | null;
  claimed_at: string | null;
  created_at: string;
};
